import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTestimonials } from '../hooks/useTestimonials';

const ReviewsPage = () => {
  const { testimonials, loading, error, averageRating, totalReviews } = useTestimonials();
  const [selectedAdventure, setSelectedAdventure] = useState('all');
  const [adventures, setAdventures] = useState([]);

  useEffect(() => {
    fetchAdventures();
  }, []);

  const fetchAdventures = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/adventures`);
      if (response.ok) {
        const data = await response.json();
        setAdventures(data);
      }
    } catch (error) {
      console.error('Error fetching adventures:', error);
    }
  };

  // Create dynamic adventure filters
  const adventureFilters = [
    { value: 'all', label: 'All Adventures' },
    ...adventures.map(adventure => ({
      value: adventure.id,
      label: adventure.title
    }))
  ];

  // Filter testimonials by selected adventure
  const filteredTestimonials = selectedAdventure === 'all' 
    ? testimonials 
    : testimonials.filter(testimonial => testimonial.adventure_id === selectedAdventure);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <span key={i} className={`text-2xl ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}>
        ⭐
      </span>
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-slate-50 to-white pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-slate-50 to-white pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-ocean-900 via-ocean-800 to-ocean-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Guest Reviews</h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto mb-8">
              Real stories from real adventurers who've experienced transformative journeys with Ahmed. 
              Discover what makes our experiences truly special.
            </p>
            
            {/* Rating Summary */}
            <div className="flex justify-center items-center space-x-8 mt-8">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">{averageRating.toFixed(1)}</div>
                <div className="flex justify-center mb-2">
                  {renderStars(averageRating)}
                </div>
                <div className="text-white/80">Average Rating</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">{totalReviews}+</div>
                <div className="text-white/80">Happy Adventurers</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">100%</div>
                <div className="text-white/80">Recommend Us</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filters - Only sticky on large screens */}
      <section className="py-8 bg-white border-b border-slate-200 lg:sticky lg:top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {adventureFilters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setSelectedAdventure(filter.value)}
                className={`px-6 py-3 rounded-full font-semibold transition-all ${
                  selectedAdventure === filter.value
                    ? 'bg-coral-500 text-white shadow-lg'
                    : 'bg-slate-100 text-slate-700 hover:bg-coral-100 hover:text-coral-700'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          
          <div className="text-center mt-4">
            <span className="text-slate-600">
              Showing {filteredTestimonials.length} {filteredTestimonials.length === 1 ? 'review' : 'reviews'}
            </span>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-3xl p-8 shadow-lg">
                  <div className="animate-pulse">
                    <div className="flex justify-center mb-4">
                      <div className="h-6 w-32 bg-slate-200 rounded"></div>
                    </div>
                    <div className="space-y-3 mb-6">
                      <div className="h-4 bg-slate-200 rounded"></div>
                      <div className="h-4 bg-slate-200 rounded w-5/6"></div>
                      <div className="h-4 bg-slate-200 rounded w-4/6"></div>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-slate-200 rounded-full mx-auto mb-4"></div>
                      <div className="h-4 bg-slate-200 rounded w-24 mx-auto mb-2"></div>
                      <div className="h-3 bg-slate-200 rounded w-20 mx-auto mb-2"></div>
                      <div className="h-6 bg-slate-200 rounded w-32 mx-auto"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">⚠️</div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">Failed to load reviews</h3>
              <p className="text-slate-600 mb-8">Please try refreshing the page</p>
              <button
                onClick={() => window.location.reload()}
                className="bg-coral-500 hover:bg-coral-600 text-white px-6 py-3 rounded-full font-semibold transition-all"
              >
                Retry
              </button>
            </div>
          ) : filteredTestimonials.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">⭐</div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">
                {testimonials.length === 0 ? 'No reviews yet' : 'No reviews found for this filter'}
              </h3>
              <p className="text-slate-600 mb-8">
                {testimonials.length === 0 
                  ? 'Be the first to share your adventure experience!' 
                  : 'Try selecting a different adventure filter'
                }
              </p>
              {testimonials.length > 0 && (
                <button
                  onClick={() => setSelectedAdventure('all')}
                  className="bg-coral-500 hover:bg-coral-600 text-white px-6 py-3 rounded-full font-semibold transition-all"
                >
                  Show All Reviews
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredTestimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  {/* Rating */}
                  <div className="flex justify-center mb-4">
                    {renderStars(testimonial.rating)}
                  </div>

                  {/* Review Text */}
                  <blockquote className="text-slate-700 text-center mb-6 leading-relaxed">
                    "{testimonial.text || testimonial.content}"
                  </blockquote>

                  {/* Reviewer Info */}
                  <div className="text-center">
                    <div className="mb-4">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-16 h-16 rounded-full object-cover mx-auto border-4 border-coral-100"
                      />
                    </div>
                    <div className="font-semibold text-slate-800 mb-1">
                      {testimonial.name}
                    </div>
                    <div className="text-slate-600 text-sm mb-2">
                      {testimonial.location}
                    </div>
                    {testimonial.adventure && (
                      <div className="inline-block bg-coral-100 text-coral-700 px-3 py-1 rounded-full text-xs font-medium">
                        {testimonial.adventure}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-20 bg-gradient-to-b from-slate-100 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">Why Adventurers Love Us</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Consistent excellence across all our adventures, backed by professional qualifications and genuine care
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              {
                icon: '🏆',
                title: 'PADI MSDT Certified',
                description: 'Master Scuba Diver Trainer with 8+ years experience'
              },
              {
                icon: '🛡️',
                title: '100% Safety Record',
                description: 'Impeccable safety standards with full insurance coverage'
              },
              {
                icon: '👥',
                title: 'Small Groups Only',
                description: 'Maximum 4 people ensures personalized attention'
              },
              {
                icon: '🌟',
                title: '5-Star Experiences',
                description: 'Consistently rated excellent by all our guests'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-4 sm:p-5 bg-white rounded-xl shadow-lg"
              >
                <div className="text-3xl sm:text-4xl mb-2">{feature.icon}</div>
                <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-1">{feature.title}</h3>
                <p className="text-slate-600 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leave Review CTA */}
      <section className="py-12 sm:py-16 bg-gradient-to-r from-coral-500 to-coral-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">Share Your Experience</h2>
          <p className="text-base sm:text-lg text-coral-100 mb-6">
            Been on an adventure with us? We'd love to hear about your experience!
          </p>
          
          <div className="bg-white/10 backdrop-blur-sm p-4 sm:p-6 rounded-2xl mb-6">
            <div className="text-3xl sm:text-4xl mb-2">⭐</div>
            <h3 className="text-xl font-bold mb-2">Leave a Review</h3>
            <p className="text-coral-100 text-sm sm:text-base mb-4">
              Your feedback helps us improve and helps others discover the magic of ocean adventures.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="mailto:ahmed@divecampadventures.com?subject=Adventure Review"
              className="bg-white text-coral-600 hover:bg-coral-50 px-6 py-3 rounded-full font-semibold transition-all transform hover:scale-105 text-sm sm:text-base"
            >
              📧 Email Review
            </a>
            <a
              href="https://instagram.com/divecampadventures"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border-2 border-white/30 hover:border-white/50 px-6 py-3 rounded-full font-semibold transition-all transform hover:scale-105 text-sm sm:text-base"
            >
              📷 Tag Us on Instagram
            </a>
          </div>
        </div>
      </section>

      {/* Book CTA */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready for Your Own Adventure?</h2>
          <p className="text-xl text-slate-300 mb-8">
            Join the community of satisfied adventurers and create your own transformative experience
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/adventures"
              className="bg-coral-500 hover:bg-coral-600 text-white px-8 py-4 rounded-full font-semibold transition-all transform hover:scale-105"
            >
              Browse Adventures
            </a>
            <a
              href="/book"
              className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border-2 border-white/30 hover:border-white/50 px-8 py-4 rounded-full font-semibold transition-all transform hover:scale-105"
            >
              Book Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReviewsPage;